import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatErr, fmtDateTime, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, EmptyState } from "@/components/shared";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { Printer, Save, ChevronLeft, FileSignature, FileDown } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const TEMPLATE_VERSION = "2026-06-07";

const DEFAULT_TERMS = `This Vehicle Rental Agreement ("Agreement") is entered into between Fair Rideshare LLC ("Owner") and the individual identified as the Renter ("Renter").

1. VEHICLE INFORMATION
The vehicle provided under this Agreement is owned by Fair Rideshare LLC and is in good working condition at the time of rental.

2. RENTAL TERM
The rental period shall begin and end as agreed upon by both parties. Any extensions or early terminations must be mutually approved in writing or electronically.

3. RENTAL PAYMENT
The Renter agrees to pay the Owner the agreed-upon rental fee in advance of the rental period. A refundable security deposit may be required and will be returned within seven (7) days after the vehicle is returned, provided there are no damages, unpaid fees, or violations.

4. USE OF VEHICLE
The Renter agrees to operate the vehicle safely and lawfully, to be the sole authorized driver unless otherwise approved by the Owner, and not to use the vehicle for racing, towing, or any illegal purpose. The vehicle must be returned in the same condition it was received, normal wear and tear excepted.

5. RESPONSIBILITY FOR FEES AND EXPENSES
The Renter is fully responsible for all fuel costs, tolls, parking fees, tickets, traffic or moving violations, and any other expenses incurred during the rental period. The Renter is also responsible for all repair costs arising from negligence, misuse, or accidents caused while the vehicle is in their possession. The Renter assumes all financial responsibility for any loss, damage, or liability.

6. MAINTENANCE AND REPAIRS
The Owner affirms that the vehicle is in safe and reliable condition at the start of the rental. The Renter must immediately notify the Owner of any mechanical issues or damages. Unauthorized repairs are not permitted without the Owner's consent.

7. INSURANCE
The Renter agrees to maintain valid auto insurance during the rental period and assumes full responsibility for any loss, damage, or liability that occurs while the vehicle is in their possession. Renter is not covered by any insurance policy owned by Fair Rideshare LLC and is strictly barred from filing any claims against Fair Rideshare LLC. Buyer must purchase insurance via rentalcover.com.

8. TERMINATION
The Owner may terminate this Agreement at any time if the Renter violates any of its terms. Upon termination, the Renter must immediately return the vehicle to the Owner.

9. INDEMNIFICATION
The Renter agrees to indemnify and hold harmless the Owner from any and all claims, damages, losses, liabilities, and expenses arising out of or related to the Renter's use or possession of the vehicle.

10. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of the State where the rental takes place.

11. ENTIRE AGREEMENT
This Agreement constitutes the entire understanding between both parties and supersedes all prior verbal or written agreements regarding this vehicle rental.

Renter is responsible for obtaining their own insurance, as they are not covered under Fair Rideshare LLC's policy. The Renter is also responsible for all tickets, tolls, fines, and any other charges they accrue during the time of rental.

By signing below, the Renter acknowledges they have read, understood, and agreed to all terms set forth in this Agreement.`;

export default function AgreementDetail() {
  const { rid } = useParams();
  const nav = useNavigate();
  const [res, setRes] = useState(null);
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [custSig, setCustSig] = useState(null);
  const [staffSig, setStaffSig] = useState(null);
  const [signedAt, setSignedAt] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    Promise.all([api.get(`/reservations/${rid}`), api.get("/settings"), api.get(`/agreements/by-reservation/${rid}`)])
      .then(([r, s, a]) => {
        setRes(r.data);
        setSettings(s.data || {});
        if (a.data?.id) {
          setTerms(a.data.terms || DEFAULT_TERMS);
          setCustSig(a.data.customer_signature || null);
          setStaffSig(a.data.staff_signature || null);
          setSignedAt(a.data.signed_at || a.data.updated_at || null);
        }
      })
      .catch((e) => toast.error(formatErr(e)));
  }, [rid]);

  const save = async () => {
    try {
      await api.post("/agreements", {
        reservation_id: rid,
        customer_signature: custSig,
        staff_signature: staffSig,
        terms,
      });
      toast.success("Agreement saved");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const downloadPdf = async () => {
    try {
      const node = document.getElementById("agreement-doc");
      if (!node) return;
      toast.info("Generating PDF…");
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "letter" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 20;
      pdf.addImage(imgData, "PNG", 20, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - position;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 20;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 20, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const name = (res?.customer?.full_name || "renter").replace(/\s+/g, "_");
      pdf.save(`agreement_${name}_${rid.slice(0, 8)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF generation failed: " + (e?.message || "unknown"));
    }
  };

  if (!res) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const c = res.customer || {};
  const v = res.vehicle || {};

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => nav(-1)} data-testid="back-button"><ChevronLeft size={16} className="mr-1" /> Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf">
            <FileDown size={14} className="mr-2" /> PDF
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-agreement">
            <Printer size={14} className="mr-2" /> Print
          </Button>
          <Button onClick={save} className="bg-black hover:bg-gray-800 text-white" data-testid="save-agreement">
            <Save size={14} className="mr-2" /> Save Agreement
          </Button>
        </div>
      </div>

      <div id="agreement-doc" className="bg-white border border-gray-200 rounded-lg p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b">
          <div className="w-10 h-10 rounded-md bg-black text-white grid place-items-center">
            <FileSignature size={20} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{settings.business_name || "Fair Rideshare"}</h1>
            <p className="text-sm text-gray-500">{settings.business_address} {settings.business_phone && `· ${settings.business_phone}`}</p>
          </div>
        </div>

        <h2 className="font-heading text-xl font-semibold mb-4">Vehicle Rental Agreement</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm mb-6">
          <div>
            <div className="overline mb-1">Renter</div>
            <div className="font-medium">{c.full_name}</div>
            <div>{c.address}</div>
            <div>{c.phone} · {c.email}</div>
            <div className="text-gray-600 text-xs mt-1">License: {c.license_number || "—"}</div>
          </div>
          <div>
            <div className="overline mb-1">Vehicle</div>
            <div className="font-medium">{v.year} {v.make} {v.model}</div>
            <div>VIN: <span className="font-mono">{v.vin || "—"}</span></div>
            <div>Plate: <span className="font-mono">{v.plate || "—"}</span></div>
            <div className="text-gray-600 text-xs mt-1">Mileage out: {Number(v.mileage || 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="overline mb-1">Pickup</div>
            <div>{fmtDateTime(res.pickup_datetime)}</div>
          </div>
          <div>
            <div className="overline mb-1">Return</div>
            <div>{fmtDateTime(res.return_datetime)}</div>
          </div>
          <div>
            <div className="overline mb-1">Rate</div>
            <div className="capitalize">{res.rate_type} @ {money(res.rate_amount)}</div>
          </div>
          <div>
            <div className="overline mb-1">Weekly Outstanding</div>
            <div className="tabular-nums font-semibold text-amber-700">{money(res.balance_due)}</div>
          </div>
        </div>

        <div className="mb-6">
          <div className="overline mb-2">Terms &amp; Conditions</div>
          <Textarea data-testid="agreement-terms" value={terms} onChange={(e) => setTerms(e.target.value)} rows={28} className="text-sm font-sans leading-relaxed" />
        </div>

        <div className="mt-8 max-w-md">
          <div className="overline mb-2">Customer signature</div>
          <SignaturePad testId="customer-signature" value={custSig} onChange={setCustSig} />
        </div>

        <div className="mt-10 pt-4 border-t border-gray-200 text-xs text-gray-500 flex flex-wrap items-center justify-between gap-2">
          <div>Template version: <span className="font-mono">{TEMPLATE_VERSION}</span></div>
          {signedAt ? (
            <div className="text-green-700 font-medium">
              ✓ Signed {new Date(signedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
            </div>
          ) : (
            <div className="text-amber-700">Not yet signed</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AgreementsList() {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/agreements").then((r) => setRows(r.data));
  }, []);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader title="Rental Agreements" subtitle="Signed contracts" testId="agreements-page-header" />
      {rows.length === 0 ? (
        <EmptyState title="No agreements yet" description="Create one from a reservation." testId="agreements-empty" />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Pickup</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50" data-testid={`agreement-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium">{a.customer_name}</td>
                  <td className="px-4 py-3">{a.vehicle_label}</td>
                  <td className="px-4 py-3">{fmtDateTime(a.pickup_datetime)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => nav(`/admin/agreements/${a.reservation_id}`)} data-testid={`open-agreement-${a.id}`}>Open</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
